import React, { useState, useEffect } from 'react';
import { Calendar, Users, Image, Plus, Check, X, LogOut, Camera, CheckSquare, Square, ChevronLeft, ChevronRight, Clock, User, Settings, Shield, Trash2, Palette } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// API helper functions
const api = {
  setToken: (token) => {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  },
  
  getToken: () => localStorage.getItem('token'),
  
  headers: () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${api.getToken()}`
  }),
  
  // Auth
  login: async (email, password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    return res.json();
  },
  
  register: async (name, email, password) => {
    const res = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password })
    });
    return res.json();
  },
  
  getCurrentUser: async () => {
    const res = await fetch(`${API_URL}/api/auth/me`, {
      headers: api.headers()
    });
    return res.json();
  },
  
  // Events
  getEvents: async () => {
    const res = await fetch(`${API_URL}/api/events`, {
      headers: api.headers()
    });
    return res.json();
  },
  
  createEvent: async (event) => {
    const res = await fetch(`${API_URL}/api/events`, {
      method: 'POST',
      headers: api.headers(),
      body: JSON.stringify(event)
    });
    return res.json();
  },
  
  deleteEvent: async (id) => {
    const res = await fetch(`${API_URL}/api/events/${id}`, {
      method: 'DELETE',
      headers: api.headers()
    });
    return res.json();
  },
  
  // Chores
  getChores: async () => {
    const res = await fetch(`${API_URL}/api/chores`, {
      headers: api.headers()
    });
    return res.json();
  },
  
  createChore: async (chore) => {
    const res = await fetch(`${API_URL}/api/chores`, {
      method: 'POST',
      headers: api.headers(),
      body: JSON.stringify(chore)
    });
    return res.json();
  },
  
  toggleChore: async (id) => {
    const res = await fetch(`${API_URL}/api/chores/${id}/toggle`, {
      method: 'PATCH',
      headers: api.headers()
    });
    return res.json();
  },
  
  deleteChore: async (id) => {
    const res = await fetch(`${API_URL}/api/chores/${id}`, {
      method: 'DELETE',
      headers: api.headers()
    });
    return res.json();
  },
  
  // Photos
  getPhotos: async () => {
    const res = await fetch(`${API_URL}/api/photos`, {
      headers: api.headers()
    });
    return res.json();
  },
  
  uploadPhoto: async (file, caption) => {
    const formData = new FormData();
    formData.append('photo', file);
    formData.append('caption', caption);
    
    const res = await fetch(`${API_URL}/api/photos`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${api.getToken()}` },
      body: formData
    });
    return res.json();
  },
  
  deletePhoto: async (id) => {
    const res = await fetch(`${API_URL}/api/photos/${id}`, {
      method: 'DELETE',
      headers: api.headers()
    });
    return res.json();
  },
  
  // Family Members
  getFamilyMembers: async () => {
    const res = await fetch(`${API_URL}/api/family-members`, {
      headers: api.headers()
    });
    return res.json();
  },
  
  createFamilyMember: async (member) => {
    const res = await fetch(`${API_URL}/api/family-members`, {
      method: 'POST',
      headers: api.headers(),
      body: JSON.stringify(member)
    });
    return res.json();
  },
  
  updateFamilyMember: async (id, member) => {
    const res = await fetch(`${API_URL}/api/family-members/${id}`, {
      method: 'PUT',
      headers: api.headers(),
      body: JSON.stringify(member)
    });
    return res.json();
  },
  
  deleteFamilyMember: async (id) => {
    const res = await fetch(`${API_URL}/api/family-members/${id}`, {
      method: 'DELETE',
      headers: api.headers()
    });
    return res.json();
  },
  
  createFamilyMember: async (member) => {
    const res = await fetch(`${API_URL}/api/family-members`, {
      method: 'POST',
      headers: api.headers(),
      body: JSON.stringify(member)
    });
    return res.json();
  },
  
  updateFamilyMember: async (id, member) => {
    const res = await fetch(`${API_URL}/api/family-members/${id}`, {
      method: 'PUT',
      headers: api.headers(),
      body: JSON.stringify(member)
    });
    return res.json();
  },
  
  deleteFamilyMember: async (id) => {
    const res = await fetch(`${API_URL}/api/family-members/${id}`, {
      method: 'DELETE',
      headers: api.headers()
    });
    return res.json();
  },
  
  // Admin
  getUsers: async () => {
    const res = await fetch(`${API_URL}/api/admin/users`, {
      headers: api.headers()
    });
    return res.json();
  },
  
  createUser: async (user) => {
    const res = await fetch(`${API_URL}/api/admin/users`, {
      method: 'POST',
      headers: api.headers(),
      body: JSON.stringify(user)
    });
    return res.json();
  },
  
  updateUser: async (id, user) => {
    const res = await fetch(`${API_URL}/api/admin/users/${id}`, {
      method: 'PUT',
      headers: api.headers(),
      body: JSON.stringify(user)
    });
    return res.json();
  },
  
  deleteUser: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/users/${id}`, {
      method: 'DELETE',
      headers: api.headers()
    });
    return res.json();
  },
  
  getStats: async () => {
    const res = await fetch(`${API_URL}/api/admin/stats`, {
      headers: api.headers()
    });
    return res.json();
  }
};

const FamilyCalendar = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [showAuth, setShowAuth] = useState(true);
  const [authMode, setAuthMode] = useState('login');
  const [currentView, setCurrentView] = useState('calendar');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Auth form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [authError, setAuthError] = useState('');
  
  // Data state
  const [events, setEvents] = useState([]);
  const [chores, setChores] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  
  // Modal state
  const [showEventModal, setShowEventModal] = useState(false);
  const [showChoreModal, setShowChoreModal] = useState(false);
  const [slideshowMode, setSlideshowMode] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  
  // Form state
  const [eventForm, setEventForm] = useState({ title: '', date: '', time: '', description: '', assigned_to: '' });
  const [choreForm, setChoreForm] = useState({ title: '', assigned_to: '', due_date: '', notes: '' });
  
  // Admin state
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [showUserModal, setShowUserModal] = useState(false);
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', is_admin: false });
  const [editingUser, setEditingUser] = useState(null);
  const [showFamilyMemberModal, setShowFamilyMemberModal] = useState(false);
  const [familyMemberForm, setFamilyMemberForm] = useState({ name: '', color: '#FFB5E8' });
  const [editingFamilyMember, setEditingFamilyMember] = useState(null);
  
  // Theme state
  const [currentTheme, setCurrentTheme] = useState('pastel'); // Default theme
  const [showThemeSelector, setShowThemeSelector] = useState(false);

  // Available themes
  const themes = {
    pastel: {
      name: 'Pastel Dreams',
      primary: '#B5B9FF',
      secondary: '#FFB5E8',
      accent: '#BFFCC6',
      background: 'linear-gradient(135deg, #FFE5F1 0%, #E5E9FF 100%)',
      cardBg: 'rgba(255, 255, 255, 0.7)',
      text: '#6B5B95',
      textLight: '#8B7BA8',
      buttonGradient: 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)',
      calendarSelected: 'linear-gradient(135deg, #B5B9FF 0%, #FFB5E8 100%)',
      eventDefault: '#FFE5F1'
    },
    ocean: {
      name: 'Ocean Breeze',
      primary: '#4A90E2',
      secondary: '#50C878',
      accent: '#00CED1',
      background: 'linear-gradient(135deg, #E0F7FA 0%, #E1F5FE 100%)',
      cardBg: 'rgba(255, 255, 255, 0.8)',
      text: '#1E3A5F',
      textLight: '#4A6FA5',
      buttonGradient: 'linear-gradient(135deg, #4A90E2 0%, #50C878 100%)',
      calendarSelected: 'linear-gradient(135deg, #4A90E2 0%, #00CED1 100%)',
      eventDefault: '#E0F7FA'
    },
    sunset: {
      name: 'Sunset Vibes',
      primary: '#FF6B6B',
      secondary: '#FFD93D',
      accent: '#FF8C42',
      background: 'linear-gradient(135deg, #FFE5E5 0%, #FFF4E0 100%)',
      cardBg: 'rgba(255, 255, 255, 0.75)',
      text: '#8B3A3A',
      textLight: '#A85858',
      buttonGradient: 'linear-gradient(135deg, #FF6B6B 0%, #FFD93D 100%)',
      calendarSelected: 'linear-gradient(135deg, #FF6B6B 0%, #FF8C42 100%)',
      eventDefault: '#FFE5E5'
    },
    forest: {
      name: 'Forest Green',
      primary: '#52B788',
      secondary: '#95D5B2',
      accent: '#74C69D',
      background: 'linear-gradient(135deg, #D8F3DC 0%, #E8F5E9 100%)',
      cardBg: 'rgba(255, 255, 255, 0.8)',
      text: '#2D6A4F',
      textLight: '#52796F',
      buttonGradient: 'linear-gradient(135deg, #52B788 0%, #95D5B2 100%)',
      calendarSelected: 'linear-gradient(135deg, #52B788 0%, #74C69D 100%)',
      eventDefault: '#D8F3DC'
    },
    lavender: {
      name: 'Lavender Fields',
      primary: '#9B59B6',
      secondary: '#E6B3FF',
      accent: '#D4A5FF',
      background: 'linear-gradient(135deg, #F3E5F5 0%, #EDE7F6 100%)',
      cardBg: 'rgba(255, 255, 255, 0.75)',
      text: '#5E3A75',
      textLight: '#7E5A95',
      buttonGradient: 'linear-gradient(135deg, #9B59B6 0%, #E6B3FF 100%)',
      calendarSelected: 'linear-gradient(135deg, #9B59B6 0%, #D4A5FF 100%)',
      eventDefault: '#F3E5F5'
    },
    cherry: {
      name: 'Cherry Blossom',
      primary: '#FF6B9D',
      secondary: '#FFC1E3',
      accent: '#FFB3D9',
      background: 'linear-gradient(135deg, #FFE5F1 0%, #FFF0F6 100%)',
      cardBg: 'rgba(255, 255, 255, 0.8)',
      text: '#C2185B',
      textLight: '#D81B60',
      buttonGradient: 'linear-gradient(135deg, #FF6B9D 0%, #FFC1E3 100%)',
      calendarSelected: 'linear-gradient(135deg, #FF6B9D 0%, #FFB3D9 100%)',
      eventDefault: '#FFE5F1'
    },
    midnight: {
      name: 'Midnight Blue',
      primary: '#5D5FEF',
      secondary: '#9D9FFF',
      accent: '#7D7FFF',
      background: 'linear-gradient(135deg, #E8E9FF 0%, #F0F1FF 100%)',
      cardBg: 'rgba(255, 255, 255, 0.85)',
      text: '#2E3192',
      textLight: '#4E51A3',
      buttonGradient: 'linear-gradient(135deg, #5D5FEF 0%, #9D9FFF 100%)',
      calendarSelected: 'linear-gradient(135deg, #5D5FEF 0%, #7D7FFF 100%)',
      eventDefault: '#E8E9FF'
    },
    peach: {
      name: 'Peachy Keen',
      primary: '#FFB347',
      secondary: '#FFDAB9',
      accent: '#FFC98B',
      background: 'linear-gradient(135deg, #FFF5E6 0%, #FFE8CC 100%)',
      cardBg: 'rgba(255, 255, 255, 0.75)',
      text: '#CC6600',
      textLight: '#E67300',
      buttonGradient: 'linear-gradient(135deg, #FFB347 0%, #FFDAB9 100%)',
      calendarSelected: 'linear-gradient(135deg, #FFB347 0%, #FFC98B 100%)',
      eventDefault: '#FFF5E6'
    }
  };

  const theme = themes[currentTheme];

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('familyCalendarTheme');
    if (savedTheme && themes[savedTheme]) {
      setCurrentTheme(savedTheme);
    }
  }, []);

  // Save theme to localStorage when it changes
  const changeTheme = (themeName) => {
    setCurrentTheme(themeName);
    localStorage.setItem('familyCalendarTheme', themeName);
    setShowThemeSelector(false);
  };

  // Load user data on mount
  useEffect(() => {
    const token = api.getToken();
    if (token) {
      loadCurrentUser();
    }
  }, []);

  // Slideshow timer
  useEffect(() => {
    if (slideshowMode && photos.length > 0) {
      const timer = setInterval(() => {
        setCurrentPhotoIndex(prev => (prev + 1) % photos.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [slideshowMode, photos.length]);

  const loadCurrentUser = async () => {
    try {
      const user = await api.getCurrentUser();
      if (user.error) {
        handleLogout();
        return;
      }
      setCurrentUser(user);
      setShowAuth(false);
      await loadAllData();
    } catch (error) {
      console.error('Load user error:', error);
      handleLogout();
    }
  };

  const loadAllData = async () => {
    try {
      const [eventsData, choresData, photosData, membersData] = await Promise.all([
        api.getEvents(),
        api.getChores(),
        api.getPhotos(),
        api.getFamilyMembers()
      ]);
      
      console.log('Loaded events:', eventsData);
      console.log('Loaded chores:', choresData);
      
      setEvents(eventsData);
      setChores(choresData);
      setPhotos(photosData);
      setFamilyMembers(membersData);
    } catch (error) {
      console.error('Load data error:', error);
    }
  };

  const loadAdminData = async () => {
    try {
      const [usersData, statsData] = await Promise.all([
        api.getUsers(),
        api.getStats()
      ]);
      
      setUsers(usersData);
      setStats(statsData);
    } catch (error) {
      console.error('Load admin data error:', error);
    }
  };

  useEffect(() => {
    if (currentUser?.is_admin && currentView === 'admin') {
      loadAdminData();
    }
  }, [currentView, currentUser]);

  const handleAuth = async () => {
    try {
      setAuthError('');
      let result;
      
      if (authMode === 'login') {
        result = await api.login(email, password);
      } else {
        result = await api.register(name, email, password);
      }
      
      if (result.error) {
        setAuthError(result.error);
        return;
      }
      
      api.setToken(result.token);
      setCurrentUser(result.user);
      setShowAuth(false);
      await loadAllData();
      
      setEmail('');
      setPassword('');
      setName('');
    } catch (error) {
      console.error('Auth error:', error);
      setAuthError('An error occurred. Please try again.');
    }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    api.setToken(null);
    setShowAuth(true);
    setCurrentView('calendar');
  };

  const addEvent = async () => {
    try {
      const newEvent = await api.createEvent(eventForm);
      if (!newEvent.error) {
        setEvents([...events, newEvent]);
        setShowEventModal(false);
        setEventForm({ title: '', date: '', time: '', description: '', assigned_to: '' });
      }
    } catch (error) {
      console.error('Add event error:', error);
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    
    try {
      await api.deleteEvent(id);
      setEvents(events.filter(e => e.id !== id));
    } catch (error) {
      console.error('Delete event error:', error);
    }
  };

  const getPersonColor = (personName) => {
    if (!personName) return '#B5B9FF'; // Default color
    const person = familyMembers.find(m => m.name === personName);
    return person ? person.color : '#B5B9FF';
  };

  const addChore = async () => {
    try {
      const newChore = await api.createChore(choreForm);
      if (!newChore.error) {
        setChores([...chores, newChore]);
        setShowChoreModal(false);
        setChoreForm({ title: '', assigned_to: '', due_date: '', notes: '' });
      }
    } catch (error) {
      console.error('Add chore error:', error);
    }
  };

  const toggleChore = async (id) => {
    try {
      const updatedChore = await api.toggleChore(id);
      if (!updatedChore.error) {
        setChores(chores.map(chore => chore.id === id ? updatedChore : chore));
      }
    } catch (error) {
      console.error('Toggle chore error:', error);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      try {
        const newPhoto = await api.uploadPhoto(file, '');
        if (!newPhoto.error) {
          setPhotos([...photos, newPhoto]);
        }
      } catch (error) {
        console.error('Upload photo error:', error);
      }
    }
  };

  const deletePhoto = async (id) => {
    if (!window.confirm('Delete this photo?')) return;
    
    try {
      await api.deletePhoto(id);
      setPhotos(photos.filter(p => p.id !== id));
    } catch (error) {
      console.error('Delete photo error:', error);
    }
  };

  const handleUserSubmit = async () => {
    try {
      if (editingUser) {
        const updated = await api.updateUser(editingUser.id, {
          name: userForm.name,
          email: userForm.email,
          is_admin: userForm.is_admin
        });
        if (!updated.error) {
          setUsers(users.map(u => u.id === editingUser.id ? updated : u));
        }
      } else {
        const newUser = await api.createUser(userForm);
        if (!newUser.error) {
          setUsers([...users, newUser]);
        }
      }
      
      setShowUserModal(false);
      setUserForm({ name: '', email: '', password: '', is_admin: false });
      setEditingUser(null);
      await loadAdminData();
    } catch (error) {
      console.error('User submit error:', error);
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Delete this user? All their data will be removed.')) return;
    
    try {
      await api.deleteUser(id);
      setUsers(users.filter(u => u.id !== id));
      await loadAdminData();
    } catch (error) {
      console.error('Delete user error:', error);
    }
  };

  const handleFamilyMemberSubmit = async () => {
    try {
      if (editingFamilyMember) {
        const updated = await api.updateFamilyMember(editingFamilyMember.id, familyMemberForm);
        if (!updated.error) {
          setFamilyMembers(familyMembers.map(m => m.id === editingFamilyMember.id ? updated : m));
        }
      } else {
        const newMember = await api.createFamilyMember(familyMemberForm);
        if (!newMember.error) {
          setFamilyMembers([...familyMembers, newMember]);
        }
      }
      
      setShowFamilyMemberModal(false);
      setFamilyMemberForm({ name: '', color: '#FFB5E8' });
      setEditingFamilyMember(null);
    } catch (error) {
      console.error('Family member submit error:', error);
    }
  };

  const deleteFamilyMember = async (id) => {
    if (!window.confirm('Delete this family member? Events assigned to them will become unassigned.')) return;
    
    try {
      await api.deleteFamilyMember(id);
      setFamilyMembers(familyMembers.filter(m => m.id !== id));
    } catch (error) {
      console.error('Delete family member error:', error);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(event => {
      // Handle both string dates and Date objects from backend
      const eventDate = typeof event.date === 'string' 
        ? event.date.split('T')[0]  // Extract just the date part if it's an ISO string
        : new Date(event.date).toISOString().split('T')[0];
      return eventDate === dateStr;
    });
  };

  const getChoresForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return chores.filter(chore => {
      // Handle both string dates and Date objects from backend
      const choreDate = typeof chore.due_date === 'string'
        ? chore.due_date.split('T')[0]
        : new Date(chore.due_date).toISOString().split('T')[0];
      return choreDate === dateStr;
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (showAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{
        background: 'linear-gradient(135deg, #FFE5F1 0%, #E5F3FF 50%, #F0E5FF 100%)',
        fontFamily: '"Plus Jakarta Sans", -apple-system, sans-serif'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(20px)',
          borderRadius: '32px',
          padding: '48px',
          maxWidth: '440px',
          width: '100%',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08), 0 0 1px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.5)'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)',
              borderRadius: '24px',
              margin: '0 auto 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(181, 185, 255, 0.3)'
            }}>
              <Calendar size={40} color="#fff" strokeWidth={2.5} />
            </div>
            <h1 style={{
              fontSize: '32px',
              fontWeight: '800',
              background: 'linear-gradient(135deg, #FF9CEE 0%, #B28DFF 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '8px',
              letterSpacing: '-0.02em'
            }}>Family Hub</h1>
            <p style={{ color: '#8B7BA8', fontSize: '15px' }}>
              {authMode === 'login' ? 'Welcome back!' : 'Create your family account'}
            </p>
          </div>

          {authError && (
            <div style={{
              background: '#FFE5E5',
              color: '#D32F2F',
              padding: '12px 16px',
              borderRadius: '12px',
              marginBottom: '16px',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              {authError}
            </div>
          )}

          {authMode === 'signup' && (
            <input
              type="text"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{
                width: '100%',
                padding: '16px 20px',
                border: '2px solid #F0E5FF',
                borderRadius: '16px',
                fontSize: '15px',
                marginBottom: '16px',
                outline: 'none',
                transition: 'all 0.2s',
                background: '#FAFBFF'
              }}
              onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
              onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
            />
          )}

          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAuth()}
            style={{
              width: '100%',
              padding: '16px 20px',
              border: '2px solid #F0E5FF',
              borderRadius: '16px',
              fontSize: '15px',
              marginBottom: '16px',
              outline: 'none',
              transition: 'all 0.2s',
              background: '#FAFBFF'
            }}
            onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
            onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
          />

          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAuth()}
            style={{
              width: '100%',
              padding: '16px 20px',
              border: '2px solid #F0E5FF',
              borderRadius: '16px',
              fontSize: '15px',
              marginBottom: '24px',
              outline: 'none',
              transition: 'all 0.2s',
              background: '#FAFBFF'
            }}
            onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
            onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
          />

          <button
            onClick={handleAuth}
            style={{
              width: '100%',
              padding: '16px',
              background: 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '16px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: '0 8px 24px rgba(181, 185, 255, 0.3)',
              letterSpacing: '0.02em'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 12px 32px rgba(181, 185, 255, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 8px 24px rgba(181, 185, 255, 0.3)';
            }}
          >
            {authMode === 'login' ? 'Sign In' : 'Create Account'}
          </button>

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <button
              onClick={() => {
                setAuthMode(authMode === 'login' ? 'signup' : 'login');
                setAuthError('');
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#B28DFF',
                fontSize: '14px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              {authMode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (slideshowMode) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        background: '#1a1a2e',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {photos.length > 0 ? (
          <>
            <img
              src={photos[currentPhotoIndex].url}
              alt="Family photo"
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: '24px',
                boxShadow: '0 24px 80px rgba(0, 0, 0, 0.5)'
              }}
            />
            <button
              onClick={() => setSlideshowMode(false)}
              style={{
                position: 'absolute',
                top: '32px',
                right: '32px',
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: 'none',
                borderRadius: '50%',
                width: '56px',
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
              onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
            >
              <X size={28} color="#fff" />
            </button>
            <div style={{
              position: 'absolute',
              bottom: '32px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              padding: '12px 24px',
              borderRadius: '24px',
              color: 'white',
              fontSize: '14px',
              fontFamily: '"Plus Jakarta Sans", sans-serif'
            }}>
              {currentPhotoIndex + 1} / {photos.length}
            </div>
          </>
        ) : (
          <div style={{ color: 'white', textAlign: 'center' }}>
            <Camera size={64} color="#666" style={{ margin: '0 auto 16px' }} />
            <p style={{ fontSize: '18px', opacity: 0.6 }}>No photos yet</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.background,
      fontFamily: '"Plus Jakarta Sans", -apple-system, sans-serif',
      padding: '20px',
      transition: 'background 0.3s ease'
    }}>
      {/* Header */}
      <header style={{
        background: theme.cardBg,
        backdropFilter: 'blur(20px)',
        borderRadius: '24px',
        padding: '20px 32px',
        marginBottom: '24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04)',
        border: '1px solid rgba(255, 255, 255, 0.5)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            background: theme.buttonGradient,
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 16px ${theme.primary}40`
          }}>
            <Calendar size={24} color="#fff" strokeWidth={2.5} />
          </div>
          <div>
            <h1 style={{
              fontSize: '24px',
              fontWeight: '800',
              color: theme.text,
              margin: 0,
              letterSpacing: '-0.02em'
            }}>Family Hub</h1>
            <p style={{ fontSize: '13px', color: theme.textLight, margin: 0 }}>
              Welcome, {currentUser.name} {currentUser.is_admin && '👑'}
            </p>
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button
            onClick={() => setShowThemeSelector(!showThemeSelector)}
            style={{
              background: theme.cardBg,
              border: `2px solid ${theme.primary}`,
              borderRadius: '12px',
              padding: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
              position: 'relative'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = theme.primary;
              e.currentTarget.querySelector('svg').style.color = '#fff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = theme.cardBg;
              e.currentTarget.querySelector('svg').style.color = theme.primary;
            }}
            title="Change theme"
          >
            <Palette size={20} color={theme.primary} strokeWidth={2.5} />
          </button>
          
          <button
            onClick={handleLogout}
            style={{
              background: `linear-gradient(135deg, ${theme.eventDefault} 0%, ${theme.cardBg} 100%)`,
              border: 'none',
              borderRadius: '12px',
              padding: '12px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            color: '#8B7BA8',
            transition: 'transform 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
        >
          <LogOut size={18} />
          Sign Out
        </button>
        </div>
      </header>

      {/* Theme Selector Dropdown */}
      {showThemeSelector && (
        <div style={{
          position: 'fixed',
          top: '100px',
          right: '32px',
          background: theme.cardBg,
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          padding: '20px',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.5)',
          zIndex: 1000,
          minWidth: '280px'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: theme.text,
            margin: '0 0 16px 0'
          }}>Choose Theme</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {Object.entries(themes).map(([key, themeOption]) => (
              <button
                key={key}
                onClick={() => changeTheme(key)}
                style={{
                  background: themeOption.background,
                  border: currentTheme === key ? `3px solid ${themeOption.primary}` : '2px solid rgba(255, 255, 255, 0.5)',
                  borderRadius: '12px',
                  padding: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  textAlign: 'center',
                  position: 'relative'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {currentTheme === key && (
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    background: themeOption.primary,
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <span style={{ color: '#fff', fontSize: '12px' }}>✓</span>
                  </div>
                )}
                
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: themeOption.buttonGradient,
                  borderRadius: '8px',
                  margin: '0 auto 8px',
                  boxShadow: `0 4px 12px ${themeOption.primary}40`
                }} />
                
                <div style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: themeOption.text
                }}>{themeOption.name}</div>
              </button>
            ))}
          </div>
          
          <button
            onClick={() => setShowThemeSelector(false)}
            style={{
              width: '100%',
              marginTop: '16px',
              padding: '12px',
              background: theme.primary,
              color: '#fff',
              border: 'none',
              borderRadius: '10px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            Close
          </button>
        </div>
      )}

      {/* Navigation */}
      <nav style={{
        background: theme.cardBg,
        backdropFilter: 'blur(20px)',
        borderRadius: '20px',
        padding: '8px',
        marginBottom: '24px',
        display: 'flex',
        gap: '8px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04)',
        border: '1px solid rgba(255, 255, 255, 0.5)'
      }}>
        {[
          { id: 'calendar', icon: Calendar, label: 'Calendar' },
          { id: 'chores', icon: CheckSquare, label: 'Chores' },
          { id: 'photos', icon: Image, label: 'Photos' },
          ...(currentUser.is_admin ? [{ id: 'admin', icon: Shield, label: 'Admin' }] : [])
        ].map(item => (
          <button
            key={item.id}
            onClick={() => setCurrentView(item.id)}
            style={{
              flex: 1,
              padding: '14px 20px',
              border: 'none',
              borderRadius: '14px',
              background: currentView === item.id
                ? theme.buttonGradient
                : 'transparent',
              color: currentView === item.id ? 'white' : theme.textLight,
              fontWeight: '700',
              fontSize: '15px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.3s',
              boxShadow: currentView === item.id ? `0 4px 16px ${theme.primary}30` : 'none'
            }}
          >
            <item.icon size={20} strokeWidth={2.5} />
            {item.label}
          </button>
        ))}
      </nav>

      {/* Calendar View - keeping existing calendar code */}
      {currentView === 'calendar' && (
        <div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            padding: '32px',
            marginBottom: '24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.5)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '32px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <button
                  onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}
                  style={{
                    background: 'linear-gradient(135deg, #FFE5F1 0%, #F0E5FF 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'transform 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.transform = 'translateX(-2px)'}
                  onMouseLeave={(e) => e.target.style.transform = 'translateX(0)'}
                >
                  <ChevronLeft size={20} color="#B28DFF" />
                </button>
                <h2 style={{
                  fontSize: '28px',
                  fontWeight: '800',
                  color: '#6B5B95',
                  margin: 0,
                  letterSpacing: '-0.02em'
                }}>
                  {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h2>
                <button
                  onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}
                  style={{
                    background: 'linear-gradient(135deg, #FFE5F1 0%, #F0E5FF 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'transform 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.transform = 'translateX(2px)'}
                  onMouseLeave={(e) => e.target.style.transform = 'translateX(0)'}
                >
                  <ChevronRight size={20} color="#B28DFF" />
                </button>
              </div>
              <button
                onClick={() => setShowEventModal(true)}
                style={{
                  background: 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)',
                  border: 'none',
                  borderRadius: '14px',
                  padding: '12px 24px',
                  color: 'white',
                  fontWeight: '700',
                  fontSize: '15px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 16px rgba(181, 185, 255, 0.3)',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                <Plus size={20} strokeWidth={2.5} />
                Add Event
              </button>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '8px'
            }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} style={{
                  textAlign: 'center',
                  fontSize: '13px',
                  fontWeight: '700',
                  color: '#B28DFF',
                  padding: '12px 0',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase'
                }}>
                  {day}
                </div>
              ))}
              
              {getDaysInMonth(currentDate).map((date, idx) => {
                const dateEvents = date ? getEventsForDate(date) : [];
                const dateChores = date ? getChoresForDate(date) : [];
                const isToday = date && date.toDateString() === new Date().toDateString();
                const isSelected = date && selectedDate && date.toDateString() === selectedDate.toDateString();
                
                return (
                  <div
                    key={idx}
                    onClick={() => date && setSelectedDate(date)}
                    style={{
                      background: !date ? 'transparent' : 
                                  isSelected ? 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)' :
                                  isToday ? 'rgba(181, 185, 255, 0.2)' : 
                                  'rgba(255, 255, 255, 0.5)',
                      borderRadius: '16px',
                      padding: '12px',
                      minHeight: '100px',
                      cursor: date ? 'pointer' : 'default',
                      border: isToday ? '2px solid #B5B9FF' : '1px solid rgba(255, 255, 255, 0.3)',
                      transition: 'all 0.2s',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      if (date && !isSelected) {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (date && !isSelected) {
                        e.currentTarget.style.background = isToday ? 'rgba(181, 185, 255, 0.2)' : 'rgba(255, 255, 255, 0.5)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    {date && (
                      <>
                        <div style={{
                          fontSize: '16px',
                          fontWeight: '700',
                          color: isSelected ? 'white' : '#6B5B95',
                          marginBottom: '8px'
                        }}>
                          {date.getDate()}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {dateEvents.slice(0, 2).map(event => {
                            const personColor = getPersonColor(event.assigned_to);
                            return (
                              <div
                                key={event.id}
                                style={{
                                  background: isSelected ? 'rgba(255, 255, 255, 0.3)' : personColor,
                                  padding: '6px 8px',
                                  borderRadius: '8px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  color: isSelected ? 'white' : '#fff',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                              >
                                {event.assigned_to && (
                                  <span style={{ marginRight: '4px' }}>👤</span>
                                )}
                                {event.title}
                              </div>
                            );
                          })}
                          {dateChores.slice(0, 1).map(chore => (
                            <div
                              key={chore.id}
                              style={{
                                background: isSelected ? 'rgba(255, 255, 255, 0.3)' : '#E5F3FF',
                                padding: '6px 8px',
                                borderRadius: '8px',
                                fontSize: '11px',
                                fontWeight: '600',
                                color: isSelected ? 'white' : '#6EB5FF',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              ✓ {chore.title}
                            </div>
                          ))}
                          {(dateEvents.length + dateChores.length > 3) && (
                            <div style={{
                              fontSize: '10px',
                              color: isSelected ? 'rgba(255, 255, 255, 0.7)' : '#B28DFF',
                              fontWeight: '600',
                              textAlign: 'center'
                            }}>
                              +{dateEvents.length + dateChores.length - 3} more
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Selected Date Details */}
          {selectedDate && (
            <div style={{
              background: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              padding: '32px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04)',
              border: '1px solid rgba(255, 255, 255, 0.5)'
            }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '800',
                color: '#6B5B95',
                marginBottom: '24px',
                letterSpacing: '-0.02em'
              }}>
                {formatDate(selectedDate)}
              </h3>
              
              {getEventsForDate(selectedDate).length === 0 && getChoresForDate(selectedDate).length === 0 ? (
                <p style={{ color: '#B28DFF', textAlign: 'center', padding: '40px 0' }}>
                  No events or chores for this day
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {getEventsForDate(selectedDate).map(event => {
                    const personColor = getPersonColor(event.assigned_to);
                    return (
                      <div
                        key={event.id}
                        style={{
                          background: `linear-gradient(135deg, ${personColor}15 0%, ${personColor}08 100%)`,
                          padding: '20px',
                          borderRadius: '16px',
                          border: `2px solid ${personColor}`,
                          position: 'relative'
                        }}
                      >
                        <button
                          onClick={() => deleteEvent(event.id)}
                          style={{
                            position: 'absolute',
                            top: '12px',
                            right: '12px',
                            background: 'rgba(255, 255, 255, 0.9)',
                            border: 'none',
                            borderRadius: '8px',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#FFE5E5';
                            e.currentTarget.style.transform = 'scale(1.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          <X size={18} color="#D32F2F" />
                        </button>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Clock size={16} color={personColor} />
                            <span style={{ fontSize: '14px', fontWeight: '600', color: personColor }}>
                              {event.time}
                            </span>
                          </div>
                          {event.assigned_to && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              background: personColor,
                              padding: '4px 12px',
                              borderRadius: '12px'
                            }}>
                              <User size={14} color="#fff" />
                              <span style={{ fontSize: '13px', fontWeight: '600', color: '#fff' }}>
                                {event.assigned_to}
                              </span>
                            </div>
                          )}
                        </div>
                        <h4 style={{
                          fontSize: '18px',
                          fontWeight: '700',
                          color: '#6B5B95',
                          margin: '0 0 8px 0'
                        }}>
                          {event.title}
                        </h4>
                        {event.description && (
                          <p style={{ fontSize: '14px', color: '#8B7BA8', margin: 0 }}>
                            {event.description}
                          </p>
                        )}
                      </div>
                    );
                  })}
                  
                  {getChoresForDate(selectedDate).map(chore => (
                    <div
                      key={chore.id}
                      style={{
                        background: 'linear-gradient(135deg, #E5F3FF 0%, #F0E5FF 100%)',
                        padding: '20px',
                        borderRadius: '16px',
                        border: '1px solid rgba(110, 181, 255, 0.3)',
                        opacity: chore.completed ? 0.6 : 1
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <button
                          onClick={() => toggleChore(chore.id)}
                          style={{
                            background: chore.completed ? 'linear-gradient(135deg, #BFFCC6 0%, #85E3FF 100%)' : 'white',
                            border: chore.completed ? 'none' : '2px solid #B5B9FF',
                            borderRadius: '10px',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            flexShrink: 0,
                            transition: 'all 0.2s'
                          }}
                        >
                          {chore.completed && <Check size={20} color="#fff" strokeWidth={3} />}
                        </button>
                        <div style={{ flex: 1 }}>
                          <h4 style={{
                            fontSize: '18px',
                            fontWeight: '700',
                            color: '#6B5B95',
                            margin: '0 0 4px 0',
                            textDecoration: chore.completed ? 'line-through' : 'none'
                          }}>
                            {chore.title}
                          </h4>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <User size={14} color="#6EB5FF" />
                            <span style={{ fontSize: '13px', color: '#8B7BA8', fontWeight: '600' }}>
                              {chore.assigned_to}
                            </span>
                          </div>
                        </div>
                      </div>
                      {chore.notes && (
                        <p style={{
                          fontSize: '14px',
                          color: '#8B7BA8',
                          margin: '12px 0 0 44px'
                        }}>
                          {chore.notes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Chores View - keeping existing chores code */}
      {currentView === 'chores' && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          padding: '32px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04)',
          border: '1px solid rgba(255, 255, 255, 0.5)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px'
          }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '800',
              color: '#6B5B95',
              margin: 0,
              letterSpacing: '-0.02em'
            }}>
              Chores
            </h2>
            <button
              onClick={() => setShowChoreModal(true)}
              style={{
                background: 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)',
                border: 'none',
                borderRadius: '14px',
                padding: '12px 24px',
                color: 'white',
                fontWeight: '700',
                fontSize: '15px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 16px rgba(181, 185, 255, 0.3)',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
            >
              <Plus size={20} strokeWidth={2.5} />
              Add Chore
            </button>
          </div>

          {chores.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#B28DFF' }}>
              <CheckSquare size={48} color="#E0D4F7" style={{ margin: '0 auto 16px' }} />
              <p style={{ fontSize: '16px', fontWeight: '600' }}>No chores yet</p>
              <p style={{ fontSize: '14px', opacity: 0.7 }}>Add your first chore to get started</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {chores.sort((a, b) => new Date(a.due_date) - new Date(b.due_date)).map(chore => (
                <div
                  key={chore.id}
                  style={{
                    background: chore.completed 
                      ? 'linear-gradient(135deg, #BFFCC6 0%, #AFF8DB 100%)'
                      : 'linear-gradient(135deg, #E5F3FF 0%, #F0E5FF 100%)',
                    padding: '24px',
                    borderRadius: '20px',
                    border: `2px solid ${chore.completed ? 'rgba(191, 252, 198, 0.5)' : 'rgba(110, 181, 255, 0.3)'}`,
                    transition: 'all 0.3s',
                    opacity: chore.completed ? 0.7 : 1
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button
                      onClick={() => toggleChore(chore.id)}
                      style={{
                        background: chore.completed ? 'linear-gradient(135deg, #85E3FF 0%, #6EB5FF 100%)' : 'white',
                        border: chore.completed ? 'none' : '2px solid #B5B9FF',
                        borderRadius: '12px',
                        width: '48px',
                        height: '48px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        flexShrink: 0,
                        transition: 'all 0.2s',
                        boxShadow: chore.completed ? '0 4px 16px rgba(110, 181, 255, 0.3)' : 'none'
                      }}
                      onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                      onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                    >
                      {chore.completed && <Check size={28} color="#fff" strokeWidth={3} />}
                    </button>
                    <div style={{ flex: 1 }}>
                      <h3 style={{
                        fontSize: '20px',
                        fontWeight: '700',
                        color: '#6B5B95',
                        margin: '0 0 8px 0',
                        textDecoration: chore.completed ? 'line-through' : 'none',
                        letterSpacing: '-0.01em'
                      }}>
                        {chore.title}
                      </h3>
                      <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <User size={16} color="#6EB5FF" />
                          <span style={{ fontSize: '14px', color: '#8B7BA8', fontWeight: '600' }}>
                            {chore.assigned_to}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Clock size={16} color="#FFB5E8" />
                          <span style={{ fontSize: '14px', color: '#8B7BA8', fontWeight: '600' }}>
                            {new Date(chore.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                      </div>
                      {chore.notes && (
                        <p style={{
                          fontSize: '14px',
                          color: '#8B7BA8',
                          margin: '12px 0 0 0',
                          lineHeight: '1.6'
                        }}>
                          {chore.notes}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Photos View - keeping existing photos code */}
      {currentView === 'photos' && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          padding: '32px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04)',
          border: '1px solid rgba(255, 255, 255, 0.5)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px'
          }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '800',
              color: '#6B5B95',
              margin: 0,
              letterSpacing: '-0.02em'
            }}>
              Family Photos
            </h2>
            <div style={{ display: 'flex', gap: '12px' }}>
              <label style={{
                background: 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)',
                border: 'none',
                borderRadius: '14px',
                padding: '12px 24px',
                color: 'white',
                fontWeight: '700',
                fontSize: '15px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 16px rgba(181, 185, 255, 0.3)',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <Camera size={20} strokeWidth={2.5} />
                Upload Photos
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  style={{ display: 'none' }}
                />
              </label>
              {photos.length > 0 && (
                <button
                  onClick={() => setSlideshowMode(true)}
                  style={{
                    background: 'linear-gradient(135deg, #FFE5F1 0%, #F0E5FF 100%)',
                    border: 'none',
                    borderRadius: '14px',
                    padding: '12px 24px',
                    color: '#B28DFF',
                    fontWeight: '700',
                    fontSize: '15px',
                    cursor: 'pointer',
                    transition: 'transform 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                >
                  Start Slideshow
                </button>
              )}
            </div>
          </div>

          {photos.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#B28DFF' }}>
              <Camera size={48} color="#E0D4F7" style={{ margin: '0 auto 16px' }} />
              <p style={{ fontSize: '16px', fontWeight: '600' }}>No photos yet</p>
              <p style={{ fontSize: '14px', opacity: 0.7 }}>Upload your first family photo</p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '20px'
            }}>
              {photos.map((photo, idx) => (
                <div
                  key={photo.id}
                  style={{
                    borderRadius: '20px',
                    overflow: 'hidden',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
                    transition: 'transform 0.2s',
                    cursor: 'pointer',
                    background: 'white',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <img
                    src={photo.url}
                    alt={photo.caption || 'Family photo'}
                    onClick={() => {
                      setCurrentPhotoIndex(idx);
                      setSlideshowMode(true);
                    }}
                    style={{
                      width: '100%',
                      height: '250px',
                      objectFit: 'cover'
                    }}
                  />
                  <button
                    onClick={() => deletePhoto(photo.id)}
                    style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      background: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '36px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      opacity: 0.8
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '1';
                      e.currentTarget.style.background = '#FFE5E5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '0.8';
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                    }}
                  >
                    <Trash2 size={18} color="#D32F2F" />
                  </button>
                  <div style={{ padding: '16px' }}>
                    <p style={{
                      fontSize: '13px',
                      color: '#8B7BA8',
                      margin: 0,
                      fontWeight: '600'
                    }}>
                      {new Date(photo.created_at).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </p>
                    <p style={{
                      fontSize: '12px',
                      color: '#B28DFF',
                      margin: '4px 0 0 0'
                    }}>
                      Uploaded by {photo.uploaded_by}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Admin View */}
      {currentView === 'admin' && currentUser.is_admin && (
        <div>
          {/* Stats Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
            marginBottom: '24px'
          }}>
            {[
              { label: 'Total Users', value: stats.users, color: '#FFB5E8' },
              { label: 'Events', value: stats.events, color: '#B5B9FF' },
              { label: 'Chores', value: stats.chores, color: '#85E3FF' },
              { label: 'Photos', value: stats.photos, color: '#BFFCC6' },
              { label: 'Completed', value: stats.completedChores, color: '#FFC9DE' }
            ].map(stat => (
              <div
                key={stat.label}
                style={{
                  background: 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: '20px',
                  padding: '24px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04)',
                  border: '1px solid rgba(255, 255, 255, 0.5)'
                }}
              >
                <div style={{
                  fontSize: '32px',
                  fontWeight: '800',
                  color: stat.color,
                  marginBottom: '8px'
                }}>
                  {stat.value || 0}
                </div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#8B7BA8'
                }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          {/* Users Management */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            padding: '32px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.5)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '32px'
            }}>
              <h2 style={{
                fontSize: '28px',
                fontWeight: '800',
                color: '#6B5B95',
                margin: 0,
                letterSpacing: '-0.02em'
              }}>
                User Management
              </h2>
              <button
                onClick={() => {
                  setEditingUser(null);
                  setUserForm({ name: '', email: '', password: '', is_admin: false });
                  setShowUserModal(true);
                }}
                style={{
                  background: 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)',
                  border: 'none',
                  borderRadius: '14px',
                  padding: '12px 24px',
                  color: 'white',
                  fontWeight: '700',
                  fontSize: '15px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 16px rgba(181, 185, 255, 0.3)',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                <Plus size={20} strokeWidth={2.5} />
                Add User
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {users.map(user => (
                <div
                  key={user.id}
                  style={{
                    background: 'linear-gradient(135deg, #FFE5F1 0%, #F0E5FF 100%)',
                    padding: '24px',
                    borderRadius: '20px',
                    border: '1px solid rgba(255, 181, 232, 0.3)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <h3 style={{
                        fontSize: '20px',
                        fontWeight: '700',
                        color: '#6B5B95',
                        margin: 0
                      }}>
                        {user.name}
                      </h3>
                      {user.is_admin && (
                        <span style={{
                          background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: '700'
                        }}>
                          ADMIN
                        </span>
                      )}
                    </div>
                    <p style={{
                      fontSize: '14px',
                      color: '#8B7BA8',
                      margin: '0 0 4px 0'
                    }}>
                      {user.email}
                    </p>
                    <p style={{
                      fontSize: '12px',
                      color: '#B28DFF',
                      margin: 0
                    }}>
                      Joined {new Date(user.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => {
                        setEditingUser(user);
                        setUserForm({
                          name: user.name,
                          email: user.email,
                          password: '',
                          is_admin: user.is_admin
                        });
                        setShowUserModal(true);
                      }}
                      style={{
                        background: 'linear-gradient(135deg, #E5F3FF 0%, #F0E5FF 100%)',
                        border: 'none',
                        borderRadius: '12px',
                        padding: '10px 20px',
                        color: '#6B5B95',
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: 'transform 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                      onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteUser(user.id)}
                      disabled={user.id === currentUser.id}
                      style={{
                        background: user.id === currentUser.id ? '#E0E0E0' : '#FFE5E5',
                        border: 'none',
                        borderRadius: '12px',
                        padding: '10px 20px',
                        color: user.id === currentUser.id ? '#999' : '#D32F2F',
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: user.id === currentUser.id ? 'not-allowed' : 'pointer',
                        transition: 'transform 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                      onMouseEnter={(e) => {
                        if (user.id !== currentUser.id) {
                          e.target.style.transform = 'translateY(-2px)';
                        }
                      }}
                      onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Family Members Management */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            padding: '32px',
            marginTop: '24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.5)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '32px'
            }}>
              <h2 style={{
                fontSize: '28px',
                fontWeight: '800',
                color: '#6B5B95',
                margin: 0,
                letterSpacing: '-0.02em'
              }}>
                Family Members
              </h2>
              <button
                onClick={() => {
                  setEditingFamilyMember(null);
                  setFamilyMemberForm({ name: '', color: '#FFB5E8' });
                  setShowFamilyMemberModal(true);
                }}
                style={{
                  background: 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)',
                  border: 'none',
                  borderRadius: '14px',
                  padding: '12px 24px',
                  color: 'white',
                  fontWeight: '700',
                  fontSize: '15px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 16px rgba(181, 185, 255, 0.3)',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <Plus size={20} strokeWidth={2.5} />
                Add Family Member
              </button>
            </div>

            {familyMembers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#B28DFF' }}>
                <Users size={48} color="#E0D4F7" style={{ margin: '0 auto 16px' }} />
                <p style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 8px 0' }}>No family members yet</p>
                <p style={{ fontSize: '14px', opacity: 0.7, margin: 0 }}>Add family members to assign events to them</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
                {familyMembers.map(member => (
                  <div
                    key={member.id}
                    style={{
                      background: `linear-gradient(135deg, ${member.color}20 0%, ${member.color}10 100%)`,
                      padding: '20px',
                      borderRadius: '16px',
                      border: `2px solid ${member.color}`,
                      position: 'relative'
                    }}
                  >
                    <div style={{
                      width: '48px',
                      height: '48px',
                      background: member.color,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '12px',
                      boxShadow: `0 4px 12px ${member.color}40`
                    }}>
                      <User size={24} color="#fff" strokeWidth={2.5} />
                    </div>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#6B5B95',
                      margin: '0 0 12px 0'
                    }}>
                      {member.name}
                    </h3>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => {
                          setEditingFamilyMember(member);
                          setFamilyMemberForm({ name: member.name, color: member.color });
                          setShowFamilyMemberModal(true);
                        }}
                        style={{
                          flex: 1,
                          background: 'rgba(255, 255, 255, 0.9)',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 12px',
                          color: '#6B5B95',
                          fontWeight: '600',
                          fontSize: '13px',
                          cursor: 'pointer',
                          transition: 'transform 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => deleteFamilyMember(member.id)}
                        style={{
                          background: 'rgba(255, 255, 255, 0.9)',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 12px',
                          color: '#D32F2F',
                          cursor: 'pointer',
                          transition: 'transform 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Event Modal */}
      {showEventModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          zIndex: 1000
        }} onClick={() => setShowEventModal(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: '32px',
              padding: '40px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 24px 80px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.5)'
            }}
          >
            <h3 style={{
              fontSize: '24px',
              fontWeight: '800',
              color: '#6B5B95',
              marginBottom: '24px',
              letterSpacing: '-0.02em'
            }}>
              Add New Event
            </h3>

            <input
              type="text"
              placeholder="Event title"
              value={eventForm.title}
              onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
              style={{
                width: '100%',
                padding: '16px 20px',
                border: '2px solid #F0E5FF',
                borderRadius: '16px',
                fontSize: '15px',
                marginBottom: '16px',
                outline: 'none',
                transition: 'all 0.2s',
                background: '#FAFBFF',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
              onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <input
                type="date"
                value={eventForm.date}
                onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                style={{
                  padding: '16px 20px',
                  border: '2px solid #F0E5FF',
                  borderRadius: '16px',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'all 0.2s',
                  background: '#FAFBFF',
                  fontFamily: 'inherit'
                }}
                onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
                onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
              />
              <input
                type="time"
                value={eventForm.time}
                onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
                style={{
                  padding: '16px 20px',
                  border: '2px solid #F0E5FF',
                  borderRadius: '16px',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'all 0.2s',
                  background: '#FAFBFF',
                  fontFamily: 'inherit'
                }}
                onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
                onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
              />
            </div>

            <select
              value={eventForm.assigned_to}
              onChange={(e) => setEventForm({ ...eventForm, assigned_to: e.target.value })}
              style={{
                width: '100%',
                padding: '16px 20px',
                border: '2px solid #F0E5FF',
                borderRadius: '16px',
                fontSize: '15px',
                marginBottom: '16px',
                outline: 'none',
                transition: 'all 0.2s',
                background: '#FAFBFF',
                fontFamily: 'inherit',
                cursor: 'pointer'
              }}
              onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
              onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
            >
              <option value="">Assign to person (optional)</option>
              {familyMembers.map(member => (
                <option key={member.id} value={member.name}>
                  {member.name}
                </option>
              ))}
            </select>

            <textarea
              placeholder="Description (optional)"
              value={eventForm.description}
              onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
              style={{
                width: '100%',
                padding: '16px 20px',
                border: '2px solid #F0E5FF',
                borderRadius: '16px',
                fontSize: '15px',
                marginBottom: '24px',
                outline: 'none',
                transition: 'all 0.2s',
                background: '#FAFBFF',
                minHeight: '100px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
              onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
            />

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowEventModal(false)}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: 'linear-gradient(135deg, #FFE5F1 0%, #F0E5FF 100%)',
                  border: 'none',
                  borderRadius: '16px',
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#B28DFF',
                  cursor: 'pointer',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                Cancel
              </button>
              <button
                onClick={addEvent}
                disabled={!eventForm.title || !eventForm.date || !eventForm.time}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: eventForm.title && eventForm.date && eventForm.time
                    ? 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)'
                    : '#E0D4F7',
                  border: 'none',
                  borderRadius: '16px',
                  fontSize: '16px',
                  fontWeight: '700',
                  color: 'white',
                  cursor: eventForm.title && eventForm.date && eventForm.time ? 'pointer' : 'not-allowed',
                  transition: 'transform 0.2s',
                  boxShadow: eventForm.title && eventForm.date && eventForm.time 
                    ? '0 8px 24px rgba(181, 185, 255, 0.3)' 
                    : 'none'
                }}
                onMouseEnter={(e) => {
                  if (eventForm.title && eventForm.date && eventForm.time) {
                    e.target.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                Add Event
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chore Modal */}
      {showChoreModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          zIndex: 1000
        }} onClick={() => setShowChoreModal(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: '32px',
              padding: '40px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 24px 80px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.5)'
            }}
          >
            <h3 style={{
              fontSize: '24px',
              fontWeight: '800',
              color: '#6B5B95',
              marginBottom: '24px',
              letterSpacing: '-0.02em'
            }}>
              Add New Chore
            </h3>

            <input
              type="text"
              placeholder="Chore title"
              value={choreForm.title}
              onChange={(e) => setChoreForm({ ...choreForm, title: e.target.value })}
              style={{
                width: '100%',
                padding: '16px 20px',
                border: '2px solid #F0E5FF',
                borderRadius: '16px',
                fontSize: '15px',
                marginBottom: '16px',
                outline: 'none',
                transition: 'all 0.2s',
                background: '#FAFBFF',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
              onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <input
                type="text"
                placeholder="Assigned to"
                value={choreForm.assigned_to}
                onChange={(e) => setChoreForm({ ...choreForm, assigned_to: e.target.value })}
                style={{
                  padding: '16px 20px',
                  border: '2px solid #F0E5FF',
                  borderRadius: '16px',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'all 0.2s',
                  background: '#FAFBFF',
                  fontFamily: 'inherit'
                }}
                onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
                onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
              />
              <input
                type="date"
                value={choreForm.due_date}
                onChange={(e) => setChoreForm({ ...choreForm, due_date: e.target.value })}
                style={{
                  padding: '16px 20px',
                  border: '2px solid #F0E5FF',
                  borderRadius: '16px',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'all 0.2s',
                  background: '#FAFBFF',
                  fontFamily: 'inherit'
                }}
                onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
                onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
              />
            </div>

            <textarea
              placeholder="Notes (optional)"
              value={choreForm.notes}
              onChange={(e) => setChoreForm({ ...choreForm, notes: e.target.value })}
              style={{
                width: '100%',
                padding: '16px 20px',
                border: '2px solid #F0E5FF',
                borderRadius: '16px',
                fontSize: '15px',
                marginBottom: '24px',
                outline: 'none',
                transition: 'all 0.2s',
                background: '#FAFBFF',
                minHeight: '100px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
              onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
            />

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowChoreModal(false)}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: 'linear-gradient(135deg, #FFE5F1 0%, #F0E5FF 100%)',
                  border: 'none',
                  borderRadius: '16px',
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#B28DFF',
                  cursor: 'pointer',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                Cancel
              </button>
              <button
                onClick={addChore}
                disabled={!choreForm.title || !choreForm.assigned_to || !choreForm.due_date}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: choreForm.title && choreForm.assigned_to && choreForm.due_date
                    ? 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)'
                    : '#E0D4F7',
                  border: 'none',
                  borderRadius: '16px',
                  fontSize: '16px',
                  fontWeight: '700',
                  color: 'white',
                  cursor: choreForm.title && choreForm.assigned_to && choreForm.due_date ? 'pointer' : 'not-allowed',
                  transition: 'transform 0.2s',
                  boxShadow: choreForm.title && choreForm.assigned_to && choreForm.due_date 
                    ? '0 8px 24px rgba(181, 185, 255, 0.3)' 
                    : 'none'
                }}
                onMouseEnter={(e) => {
                  if (choreForm.title && choreForm.assigned_to && choreForm.due_date) {
                    e.target.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                Add Chore
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Modal (Admin) */}
      {showUserModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          zIndex: 1000
        }} onClick={() => setShowUserModal(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: '32px',
              padding: '40px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 24px 80px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.5)'
            }}
          >
            <h3 style={{
              fontSize: '24px',
              fontWeight: '800',
              color: '#6B5B95',
              marginBottom: '24px',
              letterSpacing: '-0.02em'
            }}>
              {editingUser ? 'Edit User' : 'Add New User'}
            </h3>

            <input
              type="text"
              placeholder="Name"
              value={userForm.name}
              onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
              style={{
                width: '100%',
                padding: '16px 20px',
                border: '2px solid #F0E5FF',
                borderRadius: '16px',
                fontSize: '15px',
                marginBottom: '16px',
                outline: 'none',
                transition: 'all 0.2s',
                background: '#FAFBFF',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
              onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
            />

            <input
              type="email"
              placeholder="Email"
              value={userForm.email}
              onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
              style={{
                width: '100%',
                padding: '16px 20px',
                border: '2px solid #F0E5FF',
                borderRadius: '16px',
                fontSize: '15px',
                marginBottom: '16px',
                outline: 'none',
                transition: 'all 0.2s',
                background: '#FAFBFF',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
              onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
            />

            {!editingUser && (
              <input
                type="password"
                placeholder="Password"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  border: '2px solid #F0E5FF',
                  borderRadius: '16px',
                  fontSize: '15px',
                  marginBottom: '16px',
                  outline: 'none',
                  transition: 'all 0.2s',
                  background: '#FAFBFF',
                  fontFamily: 'inherit'
                }}
                onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
                onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
              />
            )}

            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '16px 20px',
              background: '#FAFBFF',
              border: '2px solid #F0E5FF',
              borderRadius: '16px',
              marginBottom: '24px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = '#B5B9FF'}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = '#F0E5FF'}
            >
              <input
                type="checkbox"
                checked={userForm.is_admin}
                onChange={(e) => setUserForm({ ...userForm, is_admin: e.target.checked })}
                style={{
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer'
                }}
              />
              <span style={{ fontSize: '15px', fontWeight: '600', color: '#6B5B95' }}>
                Admin privileges
              </span>
            </label>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowUserModal(false)}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: 'linear-gradient(135deg, #FFE5F1 0%, #F0E5FF 100%)',
                  border: 'none',
                  borderRadius: '16px',
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#B28DFF',
                  cursor: 'pointer',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                Cancel
              </button>
              <button
                onClick={handleUserSubmit}
                disabled={!userForm.name || !userForm.email || (!editingUser && !userForm.password)}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: (userForm.name && userForm.email && (editingUser || userForm.password))
                    ? 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)'
                    : '#E0D4F7',
                  border: 'none',
                  borderRadius: '16px',
                  fontSize: '16px',
                  fontWeight: '700',
                  color: 'white',
                  cursor: (userForm.name && userForm.email && (editingUser || userForm.password)) ? 'pointer' : 'not-allowed',
                  transition: 'transform 0.2s',
                  boxShadow: (userForm.name && userForm.email && (editingUser || userForm.password))
                    ? '0 8px 24px rgba(181, 185, 255, 0.3)' 
                    : 'none'
                }}
                onMouseEnter={(e) => {
                  if (userForm.name && userForm.email && (editingUser || userForm.password)) {
                    e.target.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                {editingUser ? 'Update User' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Family Member Modal */}
      {showFamilyMemberModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          zIndex: 1000
        }} onClick={() => setShowFamilyMemberModal(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: '32px',
              padding: '40px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 24px 80px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.5)'
            }}
          >
            <h3 style={{
              fontSize: '24px',
              fontWeight: '800',
              color: '#6B5B95',
              marginBottom: '24px',
              letterSpacing: '-0.02em'
            }}>
              {editingFamilyMember ? 'Edit Family Member' : 'Add Family Member'}
            </h3>

            <input
              type="text"
              placeholder="Name"
              value={familyMemberForm.name}
              onChange={(e) => setFamilyMemberForm({ ...familyMemberForm, name: e.target.value })}
              style={{
                width: '100%',
                padding: '16px 20px',
                border: '2px solid #F0E5FF',
                borderRadius: '16px',
                fontSize: '15px',
                marginBottom: '16px',
                outline: 'none',
                transition: 'all 0.2s',
                background: '#FAFBFF',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => e.target.style.borderColor = '#B5B9FF'}
              onBlur={(e) => e.target.style.borderColor = '#F0E5FF'}
            />

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#6B5B95',
                marginBottom: '8px'
              }}>
                Color
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px' }}>
                {[
                  '#FFB5E8', '#FF9CEE', '#FFCCF9', '#B28DFF', '#C5A3FF', '#AFCBFF',
                  '#AFF8DB', '#85E3FF', '#BFFCC6', '#FFC9DE', '#FFD4A3', '#F5E6CA'
                ].map(color => (
                  <div
                    key={color}
                    onClick={() => setFamilyMemberForm({ ...familyMemberForm, color })}
                    style={{
                      width: '100%',
                      aspectRatio: '1',
                      background: color,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      border: familyMemberForm.color === color ? '3px solid #6B5B95' : '2px solid rgba(255, 255, 255, 0.5)',
                      boxShadow: familyMemberForm.color === color ? '0 4px 12px rgba(107, 91, 149, 0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
                      transition: 'all 0.2s',
                      transform: familyMemberForm.color === color ? 'scale(1.1)' : 'scale(1)'
                    }}
                  />
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowFamilyMemberModal(false)}
                style={{
                  flex: 1,
                  padding: '16px 24px',
                  background: '#F5F5F5',
                  border: 'none',
                  borderRadius: '16px',
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#6B5B95',
                  cursor: 'pointer',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                Cancel
              </button>
              <button
                onClick={handleFamilyMemberSubmit}
                disabled={!familyMemberForm.name}
                style={{
                  flex: 1,
                  padding: '16px 24px',
                  background: familyMemberForm.name
                    ? 'linear-gradient(135deg, #FFB5E8 0%, #B5B9FF 100%)'
                    : '#E0D4F7',
                  border: 'none',
                  borderRadius: '16px',
                  fontSize: '16px',
                  fontWeight: '700',
                  color: 'white',
                  cursor: familyMemberForm.name ? 'pointer' : 'not-allowed',
                  transition: 'transform 0.2s',
                  boxShadow: familyMemberForm.name ? '0 8px 24px rgba(181, 185, 255, 0.3)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (familyMemberForm.name) {
                    e.target.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                {editingFamilyMember ? 'Update' : 'Add'} Member
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700;800&display=swap');
        
        * {
          box-sizing: border-box;
        }
        
        body {
          margin: 0;
          font-family: 'Plus Jakarta Sans', -apple-system, sans-serif;
        }
        
        input[type="date"]::-webkit-calendar-picker-indicator,
        input[type="time"]::-webkit-calendar-picker-indicator {
          cursor: pointer;
          filter: opacity(0.6);
        }
        
        input[type="date"]::-webkit-calendar-picker-indicator:hover,
        input[type="time"]::-webkit-calendar-picker-indicator:hover {
          filter: opacity(1);
        }
      `}</style>
    </div>
  );
};

export default FamilyCalendar;